import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import api from '../services/api';

const TYPE_OPTIONS = [
  { value: '', label: 'All alert types' },
  { value: 'rescheduled', label: 'Rescheduled' },
  { value: 'tutor_no_show_risk', label: 'Tutor no-show risk' },
  { value: 'tutor_no_show', label: 'Tutor no-show' },
  { value: 'student_missed', label: 'Student missed lesson' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: '', label: 'All statuses' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
];

const ASSIGNED_OPTIONS = [
  { value: '', label: 'All assignments' },
  { value: 'me', label: 'Assigned to me' },
  { value: 'unassigned', label: 'Unassigned' },
];

const typeLabel = (value) => TYPE_OPTIONS.find((item) => item.value === value)?.label || String(value || '-');
const statusLabel = (value) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const severityColor = (severity) => {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  if (severity === 'info') return 'info';
  return 'default';
};

const statusColor = (status) => {
  if (status === 'open') return 'error';
  if (status === 'acknowledged') return 'warning';
  if (status === 'resolved') return 'success';
  if (status === 'dismissed') return 'default';
  return 'default';
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatPerson = (person) => person?.name || person?.email || '-';
const reasonStatusLabel = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'complete') return 'Reasons complete';
  if (normalized === 'partial') return 'Partial reasons';
  return 'Awaiting reasons';
};

const reasonStatusColor = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'complete') return 'success';
  if (normalized === 'partial') return 'warning';
  return 'error';
};
const roleAtLessonLabel = (value) => {
  if (value === 'present_party') return 'Present party';
  if (value === 'missed_party') return 'Missed party';
  return statusLabel(value);
};

const LessonAlerts = () => {
  const [filters, setFilters] = useState({ status: 'open', type: '', severity: '', assigned: '' });
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [noteById, setNoteById] = useState({});

  const queryParams = useMemo(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    params.limit = 200;
    return params;
  }, [filters]);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, summaryRes] = await Promise.all([
        api.get('/lesson-alerts/admin', { params: queryParams }),
        api.get('/lesson-alerts/admin/summary'),
      ]);
      setAlerts(Array.isArray(listRes.data?.items) ? listRes.data.items : []);
      setSummary(summaryRes.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load lesson alerts.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const updateAlert = async (alertId, payload) => {
    setActionLoading(`${alertId}:${payload.status || 'note'}`);
    setError('');
    try {
      await api.patch(`/lesson-alerts/admin/${alertId}`, payload);
      await loadAlerts();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to update lesson alert.');
    } finally {
      setActionLoading('');
    }
  };

  const runScan = async () => {
    setActionLoading('scan');
    setError('');
    try {
      await api.post('/lesson-alerts/admin/scan');
      await loadAlerts();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to scan lessons.');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={2}>
        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Box className="lesson-alert-summary-row">
                <Box>
                  <Typography className="lesson-alert-section-label">Current alert status</Typography>
                  {summary ? (
                    <Box className="lesson-alert-summary-chips">
                      <Chip color="error" label={`Open: ${summary.totalOpen || 0}`} />
                      <Chip color="error" variant="outlined" label={`Critical: ${summary.critical || 0}`} />
                      <Chip color="warning" variant="outlined" label={`Warning: ${summary.warning || 0}`} />
                      <Chip color="info" variant="outlined" label={`Info: ${summary.info || 0}`} />
                    </Box>
                  ) : null}
                </Box>
                <Button variant="contained" onClick={runScan} disabled={actionLoading === 'scan'}>
                  {actionLoading === 'scan' ? 'Scanning...' : 'Run Scan'}
                </Button>
              </Box>

              <Box className="lesson-alert-filters">
                <Typography className="lesson-alert-section-label">Filter alerts</Typography>
                <Box className="lesson-alert-filter-controls">
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={filters.type}
                  onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}
                >
                  {TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Severity</InputLabel>
                <Select
                  label="Severity"
                  value={filters.severity}
                  onChange={(event) => setFilters((prev) => ({ ...prev, severity: event.target.value }))}
                >
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Assignment</InputLabel>
                <Select
                  label="Assignment"
                  value={filters.assigned}
                  onChange={(event) => setFilters((prev) => ({ ...prev, assigned: event.target.value }))}
                >
                  {ASSIGNED_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={loadAlerts} disabled={loading}>Refresh</Button>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : null}

        <Card>
          <CardHeader title="Alert Queue" subheader={`${alerts.length} alert${alerts.length === 1 ? '' : 's'} loaded`} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Alert</TableCell>
                  <TableCell>Lesson</TableCell>
                  <TableCell>People</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Admin Note</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Alert severity="success">No lesson alerts match the current filters.</Alert>
                    </TableCell>
                  </TableRow>
                ) : alerts.map((alert) => {
                  const noteValue = noteById[alert._id] ?? alert.adminNote ?? '';
                  const busy = actionLoading.startsWith(`${alert._id}:`);
                  return (
                    <TableRow key={alert._id} hover>
                      <TableCell>
                        <Stack spacing={0.7}>
                          <Typography fontWeight={800}>{typeLabel(alert.type)}</Typography>
                          <Typography variant="body2" color="text.secondary">{alert.message}</Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <Chip size="small" color={severityColor(alert.severity)} label={statusLabel(alert.severity)} />
                            {['tutor_no_show', 'student_missed'].includes(alert.type) ? (
                              <Chip size="small" color={reasonStatusColor(alert.reasonStatus)} label={reasonStatusLabel(alert.reasonStatus)} />
                            ) : null}
                            {alert.tutorPenaltyRecommended ? (
                              <Chip size="small" color="error" variant="outlined" label={`Tutor miss risk: ${alert.tutorMissCountWindow || 0}`} />
                            ) : null}
                            {alert.bookingNumber ? <Chip size="small" variant="outlined" label={alert.bookingNumber} /> : null}
                          </Stack>
                          {Array.isArray(alert.reasonReports) && alert.reasonReports.length ? (
                            <Stack spacing={0.7} sx={{ mt: 1 }}>
                              {alert.reasonReports.map((report, index) => (
                                    <Box key={`${report.userId || report.party}-${index}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1, bgcolor: 'background.default' }}>
                                      <Typography variant="caption" fontWeight={800} color="text.secondary">
                                        {statusLabel(report.party)} · {roleAtLessonLabel(report.roleAtLesson)} · {report.userName || report.userId || 'User'}
                                      </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{report.reason}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : ['tutor_no_show', 'student_missed'].includes(alert.type) ? (
                            <Typography variant="caption" color="error.main" display="block" sx={{ mt: 1 }}>
                              No missed-lesson reasons submitted yet.
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{alert.subject || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(alert.lessonStartAt)}</Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {alert.deliveryMode || '-'} · {alert.program || 'general'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">Tutor: {formatPerson(alert.tutor)}</Typography>
                        <Typography variant="body2">Student: {formatPerson(alert.student)}</Typography>
                        {alert.assignedTo ? (
                          <Typography variant="caption" color="text.secondary">Assigned: {formatPerson(alert.assignedTo)}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(alert.status)} label={statusLabel(alert.status)} />
                        <Typography variant="caption" display="block" color="text.secondary">
                          Created: {formatDate(alert.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          value={noteValue}
                          onChange={(event) => setNoteById((prev) => ({ ...prev, [alert._id]: event.target.value }))}
                          placeholder="Add admin note"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack spacing={1} alignItems="flex-end">
                          <AdminActionGroup align="end">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy || alert.status === 'acknowledged'}
                              onClick={() => updateAlert(alert._id, { status: 'acknowledged', adminNote: noteValue })}
                            >
                              Acknowledge
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy || alert.status === 'resolved'}
                              onClick={() => updateAlert(alert._id, { status: 'resolved', adminNote: noteValue })}
                            >
                              Resolve
                            </Button>
                            <Button
                              size="small"
                              color="inherit"
                              variant="outlined"
                              disabled={busy || alert.status === 'dismissed'}
                              onClick={() => updateAlert(alert._id, { status: 'dismissed', adminNote: noteValue })}
                            >
                              Dismiss
                            </Button>
                          </AdminActionGroup>
                          <Button
                            size="small"
                            variant="text"
                            disabled={busy}
                            onClick={() => updateAlert(alert._id, { adminNote: noteValue })}
                          >
                            Save Note
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Stack>
    </DashboardLayout>
  );
};

export default LessonAlerts;
